import { createUsePuck, Puck } from '@puckeditor/core';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import MicrositeWrapper from '~/components/MicrositeWrapper';
import { DEMO_CONFIG } from '~/demo';
import { PUCK_CONFIG, type PuckConfig, type PuckData } from '~/editor/config';
import { TemplatePlugin } from '~/editor/plugin/TemplatePlugin';
import { ListingPlugin } from '~/editor/plugin/ListingPlugin';
import '@puckeditor/core/puck.css';
import { useState } from 'react';
import type { SerializedPuckData } from '~/editor/SerializedPuckData';

const usePuck = createUsePuck<PuckConfig>();

interface PuckEditorProps {
	initialData: PuckData;
	onPublish?: (data: SerializedPuckData) => void;
}

export default function PuckEditor({ initialData, onPublish }: PuckEditorProps) {
	const [height, setHeight] = useState(0);

	return (
		<Puck
			overrides={{
				preview: ({ children }) => {
					return (
						<div
							style={{ position: 'relative', height: '100%' }}
							ref={(el) => {
								if (el && !height) {
									const rect = el.getBoundingClientRect();

									setHeight(Math.ceil(rect.height));
								}
							}}
						>
							{children}
						</div>
					);
				},
				iframe: ({ children, document }) => {
					const cache = createCache({
						key: 'puck',
						container: document?.body,
					});

					const data = usePuck((p) => p.appState.data);

					return (
						<CacheProvider value={cache}>
							<link href={DEMO_CONFIG.theme.font.family.url} rel="stylesheet" />
							<MicrositeWrapper config={DEMO_CONFIG} data={data} zoom={height}>
								{children}
							</MicrositeWrapper>
						</CacheProvider>
					);
				},
			}}
			config={PUCK_CONFIG}
			data={initialData}
			onPublish={(data) => {
				onPublish?.(JSON.parse(JSON.stringify(data)) as SerializedPuckData);
			}}
			plugins={[TemplatePlugin, ListingPlugin]}
		/>
	);
}
